import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { mediaApi, youtubeApi } from '../services/api';

import { sha256 as nobleSha256 } from '@noble/hashes/sha2';

/**
 * True streaming SHA-256 using @noble/hashes incremental API.
 *
 * Feeds the file in 8 MB slices into an incremental hasher — only one chunk
 * lives in memory at a time regardless of file size. A 10 GB file uses the
 * same ~8 MB of RAM as a 1 MB file. Yields to the UI between chunks so the
 * "Preparing upload…" indicator stays responsive.
 *
 * Returns a lowercase hex digest, or null on error.
 */
const HASH_CHUNK_BYTES = 8 * 1024 * 1024; // 8 MB

async function computeFileSha256(file) {
    if (typeof file.slice !== 'function') return null;
    try {
        const hasher = nobleSha256.create();
        let offset = 0;
        while (offset < file.size) {
            const end = Math.min(offset + HASH_CHUNK_BYTES, file.size);
            const buf = await file.slice(offset, end).arrayBuffer();
            hasher.update(new Uint8Array(buf));   // incremental — chunk discarded after this
            offset = end;
            await new Promise(r => setTimeout(r, 0)); // yield to UI
        }
        return Array.from(hasher.digest())
            .map(b => b.toString(16).padStart(2, '0')).join('');
    } catch {
        return null;
    }
}

/**
 * Videos at or above this size bypass the R2 staging path for YouTube and are
 * uploaded directly from the browser to a YouTube resumable upload URI.
 *
 * Threshold: 500 MB
 *   < 500 MB → R2 (browser → R2 directly) → backend streams to YouTube
 *   ≥ 500 MB → browser → YouTube directly (R2 skipped for YouTube entirely)
 *              Other platforms in the same post still use R2 with deduplication
 *              (one R2 upload shared across Instagram / Facebook / Twitter etc.)
 */
export const LARGE_VIDEO_THRESHOLD_BYTES = 500 * 1024 * 1024; // 500 MB

const UploadContext = createContext();

export const useUpload = () => {
    const context = useContext(UploadContext);
    if (!context) {
        throw new Error('useUpload must be used within an UploadProvider');
    }
    return context;
};

export const UploadProvider = ({ children }) => {
    const [uploads, setUploads] = useState({});

    // In-flight uploads keyed by fingerprint (sha256 when available, else
    // name__size__lastModified). A second uploadFile() call for the same
    // fingerprint awaits the first instead of starting a parallel upload.
    const inflightRef = useRef(new Map());

    const updateUpload = useCallback((id, updates) => {
        setUploads(prev => ({
            ...prev,
            [id]: { ...prev[id], ...updates }
        }));
    }, []);

    const uploadFile = async (file, onComplete) => {
        const uploadId = Math.random().toString(36).substring(7);
        const CHUNK_SIZE = 50 * 1024 * 1024; // 50MB per chunk
        const fallbackKey = `${file.name}__${file.size}__${file.lastModified}`;

        // If an identical upload is already in flight, await its result.
        const existing = inflightRef.current.get(fallbackKey);
        if (existing) {
            setUploads(prev => ({
                ...prev,
                [uploadId]: {
                    id: uploadId, filename: file.name,
                    progress: 0, status: 'preparing', error: null,
                }
            }));
            try {
                const { url, mediaId } = await existing;
                updateUpload(uploadId, { status: 'success', progress: 100, publicUrl: url, mediaId });
                if (onComplete) onComplete(url, mediaId);
            } catch (err) {
                updateUpload(uploadId, { status: 'failed', error: err.message || 'Upload failed' });
                if (onComplete) onComplete(null, null);
            }
            return;
        }

        setUploads(prev => ({
            ...prev,
            [uploadId]: {
                id: uploadId,
                filename: file.name,
                progress: 0,
                status: 'preparing',
                error: null
            }
        }));

        const run = async () => {
            // 0. Compute SHA-256 — enables server-side dedup by content hash.
            const sha256 = await computeFileSha256(file);

            updateUpload(uploadId, { status: 'uploading' });

            // 1. Initiate Multipart Upload (or receive an existing mediaId if
            //    the file was already uploaded and its R2 object still exists).
            const initRes = await mediaApi.initiateUpload(file.name, file.type, sha256);

            if (initRes.alreadyUploaded) {
                updateUpload(uploadId, {
                    status: 'success', progress: 100,
                    mediaId: initRes.mediaId, publicUrl: initRes.publicUrl,
                });
                return { url: initRes.publicUrl, mediaId: initRes.mediaId };
            }
            if (initRes.alreadyUploading) {
                // Another tab / double-click is finishing the same upload.
                // Surface a friendly error so the caller can retry — we
                // deliberately do NOT race a parallel multipart upload.
                throw new Error('This file is still uploading in another tab. Please wait a moment and try again.');
            }

            const { uploadId: r2UploadId, mediaId, r2Path } = initRes;
            updateUpload(uploadId, { mediaId });

            const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
            const uploadedParts = [];

            // 2. Upload Chunks
            for (let i = 0; i < totalChunks; i++) {
                const start = i * CHUNK_SIZE;
                const end = Math.min(start + CHUNK_SIZE, file.size);
                const chunk = file.slice(start, end);
                const partNumber = i + 1;

                const { url } = await mediaApi.presignPart(r2Path, r2UploadId, partNumber);

                const uploadPartRes = await axios.put(url, chunk, { // foxguard: ignore[js/no-ssrf]
                    headers: { 'Content-Type': file.type },
                    onUploadProgress: (progressEvent) => {
                        const chunkProgress = (progressEvent.loaded / progressEvent.total) * (1 / totalChunks) * 100;
                        const overallProgress = (i / totalChunks) * 100 + chunkProgress;
                        updateUpload(uploadId, { progress: Math.min(Math.round(overallProgress), 99) });
                    }
                });

                const etag = uploadPartRes.headers.etag;
                uploadedParts.push({ ETag: etag, PartNumber: partNumber });
            }

            // 3. Complete Multipart Upload
            updateUpload(uploadId, { status: 'verifying' });
            const completeRes = await mediaApi.completeUpload(r2UploadId, r2Path, mediaId, uploadedParts);

            updateUpload(uploadId, {
                status: 'success', progress: 100, publicUrl: completeRes.url,
            });

            return { url: completeRes.url, mediaId };
        };

        const runPromise = run();
        inflightRef.current.set(fallbackKey, runPromise);
        try {
            const { url, mediaId } = await runPromise;
            if (onComplete) onComplete(url, mediaId);
        } catch (error) {
            console.error('Upload failed:', error);
            updateUpload(uploadId, {
                status: 'failed',
                error: error.response?.data?.error || error.message || 'Upload failed'
            });
            if (onComplete) onComplete(null, null);
        } finally {
            inflightRef.current.delete(fallbackKey);
        }
    };

    /**
     * Upload a large video (≥ LARGE_VIDEO_THRESHOLD_BYTES) directly from the
     * browser to YouTube using a resumable upload URI.
     *
     * Flow:
     *   1. Backend creates a YouTube resumable session → returns self-authenticated URI.
     *   2. Browser PUTs the raw video file to that URI (chunked, with XHR progress).
     *   3. YouTube returns the new video ID in the response body.
     *   4. Caller is responsible for calling youtubeApi.completeDirectUpload() to
     *      save metadata to Firestore.
     *
     * @param {File}     file            — original File object from the file picker
     * @param {object}   youtubePayload  — { title, description, tags, categoryId,
     *                                       privacyStatus, scheduledFor, contentType }
     * @param {Function} onProgress      — (pct: number) => void  (0-100)
     * @returns {Promise<string>}  YouTube video ID
     */
    const youtubeDirectUpload = useCallback(async (file, youtubePayload, onProgress) => {
        // 1. Ask backend to create a resumable upload session
        const { uploadUri } = await youtubeApi.getResumableUri({
            title:         youtubePayload.title        || '',
            description:   youtubePayload.description  || '',
            tags:          youtubePayload.tags          || [],
            categoryId:    youtubePayload.categoryId    || '22',
            privacyStatus: youtubePayload.privacyStatus || 'public',
            scheduledFor:  youtubePayload.scheduledFor  || null,
            contentType:   youtubePayload.contentType   || 'video',
            fileSizeBytes: file.size,
            mimeType:      file.type || 'video/mp4',
        });

        // 2. PUT the file directly to YouTube's resumable URI using XHR so we
        //    get granular upload progress.  No Authorization header needed —
        //    the URI is self-authenticated by YouTube.
        const videoId = await new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open('PUT', uploadUri);
            xhr.setRequestHeader('Content-Type', file.type || 'video/mp4');

            xhr.upload.onprogress = (e) => {
                if (e.lengthComputable && onProgress) {
                    onProgress(Math.min(99, Math.round((e.loaded / e.total) * 100)));
                }
            };

            xhr.onload = () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                    try {
                        const resp = JSON.parse(xhr.responseText);
                        if (resp.id) {
                            if (onProgress) onProgress(100);
                            resolve(resp.id);
                        } else {
                            reject(new Error('YouTube upload succeeded but no video ID returned'));
                        }
                    } catch {
                        reject(new Error('YouTube upload response could not be parsed'));
                    }
                } else {
                    reject(new Error(`YouTube direct upload failed: HTTP ${xhr.status}`));
                }
            };

            xhr.onerror = () => reject(new Error('YouTube direct upload network error'));
            xhr.ontimeout = () => reject(new Error('YouTube direct upload timed out'));

            xhr.send(file);
        });

        return videoId;
    }, []);

    const removeUpload = (id) => {
        setUploads(prev => {
            const next = { ...prev };
            delete next[id];
            return next;
        });
    };

    return (
        <UploadContext.Provider value={{ uploads, uploadFile, youtubeDirectUpload, removeUpload }}>
            {children}
        </UploadContext.Provider>
    );
};
